import React, { useState, useEffect, useContext, createContext } from 'react';
import { AuthState, AuthContextType, LoginCredentials, User } from '../types/authTypes';
import authService from '../services/authService';
import employeeService from '../services/employeeService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    is_tfa: false,
    isLoading: true,
    error: null,
    company_id: null,
    is_cloud_active: false,
  });
  const [permissions, setPermissions] = useState<any>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authService.getToken();
        const user = authService.getUser();
        const companyId = authService.getCompanyId();
        
        if (token && user) {
          // Verify token is still valid
          try {
            setAuth({
              user: user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              is_tfa: user.istotpenabled,
              company_id: companyId,
              is_cloud_active: user.is_cloud_active || false
            });
          } catch (error) {
            // Token is invalid, clear auth
            authService.clearAuth();
            setAuth({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              is_tfa: false,
              company_id: companyId,
              is_cloud_active: false
            });
          }
        } else {
          setAuth(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        setAuth({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Failed to initialize authentication',
          is_tfa: false,
          company_id: null,
          is_cloud_active: false
        });
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!auth.user?.id) return;
    if (permissions) return; // Already have permissions

    const fetchPermissions = async () => {
      const perms = await employeeService.getEmployeePermissions(auth.user?.id || '');
      setPermissions(perms);
      setAuth(prev => ({
        ...prev,
        user: { ...prev.user!, permissions: perms }
      }));
    };
    fetchPermissions();
  }, [auth.user?.id, permissions]);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    try {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }));
      const response = await authService.login(credentials);
      
      authService.setToken(response.data.token);
      authService.setUser(response.data.user);
      
      // Store company_id if available
      if (response.data.user.company_id) {
        authService.setCompanyId(response.data.user.company_id);
      }
      
      setAuth({
        user: response.data.user,
        token: response.data.token,
        company_id: response.data.user.company_id || null,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        is_tfa: response.data.user.istotpenabled,
        is_cloud_active: response.data.user.is_cloud_active || false
      });
      return response.data.user; // <-- add this line
    } catch (error: any) {
      setAuth(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Login failed',
      }));
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem('tfa_verified'); // <-- Add this line
    setAuth({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      is_tfa: false,
      company_id: null,
      is_cloud_active: false
    });
  };


  const clearError = () => {
    setAuth(prev => ({ ...prev, error: null }));
  };

  const getCurrentCompanyId = (): number | null => {
    return authService.getCompanyId();
  };

  const value: AuthContextType = {
    auth,
    login,
    logout,
    clearError,
    permissions,
    getCurrentCompanyId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 